import { IRole } from "api/types/_role";
import { IUserOperator } from "api/types/_user";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import TooltipCustom from "components/Common/TooltipCustom";
import { useRole } from "components/Hooks/UserHooks";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import TableContainer from "../../../components/Common/TableContainer";
import OperatorForm from "./OperatorForm";

import {
  deleteUser,
  getUsers as onGetUsers,
  putUser,
} from "../../../store/thunks";
import { formatDateStringToKorea } from "helpers/format";
import {
  LIST_AGENCY_OPTION,
  LIST_OPERATOR_ROLE_OPTION,
} from "helpers/constans";
import DropdownAsyncAgency from "components/Common/DropdownAsyncAgency";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { getLoggedInUserInfo } from "api/axiosClient";
import { push } from "helpers/navigation";
import { ROUTES } from "routes/allRoutes";

interface Option {
  label: string;
  value: string;
}

const TYPE_SELECT_DEFAULT: string = "createdAt";

registerLocale("en", en);
registerLocale("ko", ko);

const UserList = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const navigate = useNavigate();
  const data = getLoggedInUserInfo();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    sort_by: withDefault(StringParam, TYPE_SELECT_DEFAULT),
    keyword: withDefault(StringParam, ""),
    agency_id: withDefault(StringParam, ""),
    role: withDefault(StringParam, ""),
  });
  const [keywordSearch, setKeywordSearch] = useState<string>(
    query?.keyword || ""
  );

  const [roleSearch, setRoleSearch] = useState<any>(
    LIST_OPERATOR_ROLE_OPTION?.find((item) => item?.value === query?.role)
      ?.value || ""
  );

  const [agencySearch, setAgencySearch] = useState<Option | any>(
    LIST_AGENCY_OPTION?.find((item) => item?.value === query?.agency_id)
      ?.value || {
      label: t("All Filter"),
      value: "all",
    }
  );

  const [userEdit, setUserEdit] = useState<
    (IUserOperator & { isCopy?: boolean }) | null
  >(null);

  const [isOpenConfirm, setIsConfirm] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [initialValuesDefault, setInitialValuesDefault] =
    useState<IUserOperator | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.User;

  const UserProperties = createSelector(selectLayoutState, (state) => ({
    users: state.users,
    isUserSuccess: state.isUserSuccess,
    isUserLoading: state.isUserLoading,
    error: state.error,
  }));

  // Inside your component
  const { users, error, isUserLoading } = useSelector(UserProperties);

  useEffect(() => {
    dispatch(onGetUsers(query));
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      keyword: keywordSearch || undefined,
      role: roleSearch || "",
      ...(agencySearch?.value !== "all"
        ? { agency_id: agencySearch?.value }
        : { agency_id: "" }),
      sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: undefined,
        role: "",
        sort_by: TYPE_SELECT_DEFAULT,
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setKeywordSearch((_prev) => "");
    setRoleSearch("");
    setAgencySearch({
      label: t("All"),
      value: "",
    });
  };

  // Begin::Update User
  const onCloseClick = () => {
    setUserEdit((_prev) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(onGetUsers(query));
  };
  //End::Update User

  // Begin::Delete
  const handleConfirmDelete = (item: IUserOperator) => {
    setInitialValuesDefault((_prev) => item);
    setIsConfirm((_prev) => true);
  };

  const handleActionDelete = async () => {
    if (!initialValuesDefault?.id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await deleteUser(initialValuesDefault?.id);
      if (response?.statusCode === 200) {
        dispatch(onGetUsers(query));
        setIsConfirmLoading((_prev) => false);
        setIsConfirm((_prev) => false);
        setInitialValuesDefault((_prev) => null);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseConfirmClick = () => {
    setInitialValuesDefault((_prev) => null);
    setIsConfirm((_prev) => false);
  };
  // End::Delete

  // Begin::Change status
  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    status: number | boolean;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await putUser(item?.id, {
        status: item?.status === 1 ? 1 : 0,
      });
      if (response?.statusCode === 200) {
        dispatch(onGetUsers(query));
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Change status

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        accessor: "id",
        filterable: true,
        sortable: false,
        thWidth: 60,
        Cell: ({ ...props }) => {
          return (
            <div style={{ minWidth: "40px" }}>
              {users?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 60,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={cell?.value === 1}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    status: !!value ? 1 : 0,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "100px" }}>{item?.name || "-"}</div>
            </>
          );
        },
      },
      {
        Header: t("Email"),
        accessor: "email",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "100px" }}>{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("Role"),
        accessor: "role",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const role = cell?.row?.original?.role;
          return (
            <>
              <div style={{ minWidth: "100px" }}>
                {t(
                  LIST_OPERATOR_ROLE_OPTION?.find(
                    (item) => item?.value === role
                  )?.label || ""
                )}
              </div>
            </>
          );
        },
      },
      {
        Header: `${t("Agency")}`,
        accessor: "AgencyName",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const agency: any = cell?.row?.original?.agency;
          return (
            agency && (
              <div className="text-center" key={agency?.id}>
                <div>{agency?.name}</div>
                <div className="text-primary">{agency?.code}</div>
              </div>
            )
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              {isHavePermissionRole(
                ROLES_FOR_APP.OPERATOR_UPDATE,
                userPermissions
              ) && (
                <TooltipCustom
                  title={t("Button Update Operator")}
                  id={`update-us-${item?.id}`}
                >
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setUserEdit((prev) => item);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
              {isHavePermissionRole(
                ROLES_FOR_APP.OPERATOR_DELETE,
                userPermissions
              ) && (
                <TooltipCustom
                  title={t("Button Delete Operator")}
                  id={`detail-cp-${item?.id}`}
                >
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConfirmDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
            </ul>
          );
        },
      },
    ],
    [i18n?.language, query, users]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
  }, []);

  useEffect(() => {
    if (
      data?.user?.role === "agency" &&
      isHavePermissionRole([ROLES_FOR_APP.AGENCY_LIST], userPermissions) ===
        false
    ) {
      push(ROUTES.AGENCY_PREPAYMENT);
    }
  }, [data]);

  useEffect(() => {
    document.title = `${t("Operator Management")} | Picaverse Agency Star+`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Operator Management")} pageTitle={t("Home")} />
          <Row>
            {isHavePermissionRole(
              ROLES_FOR_APP.OPERATOR_LIST,
              userPermissions
            ) && (
              <Col lg={12}>
                <Card
                  id="operatorList"
                  style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                >
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-5 con-sm-6">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t("Total")}:</span>
                            <CountUp
                              start={0}
                              end={users?.pagination?.total || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                      <div className="col-7 con-sm-6 text-end">
                        {isHavePermissionRole(
                          ROLES_FOR_APP.OPERATOR_CREATE,
                          userPermissions
                        ) && (
                          <div className="col-sm-auto">
                            <div>
                              <button
                                type="button"
                                className="btn btn-success add-btn"
                                // className="btn btn-success add-btn rounded-pill"
                                id="create-btn"
                                onClick={() => setIsOpen((_prev) => true)}
                              >
                                <i className="ri-add-line align-bottom me-1"></i>
                                {t("Button Create Operator")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </Row>
                  </CardHeader>
                  <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                    <Row className="g-4 align-items-center mt-0">
                      <Col
                        sm={6}
                        md={6}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("Name")}, ${t("Email")}`}
                          isShow={true}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}, ${t("Email")}...`}
                            value={keywordSearch}
                            onChange={(e) => setKeywordSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Role")} isShow={true}>
                          <DropdownOptionNew
                            name="role-search"
                            onChangeSelect={(event: any) =>
                              setRoleSearch(event?.value)
                            }
                            initialValue={roleSearch}
                            dataList={LIST_OPERATOR_ROLE_OPTION}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            placeholder={`${t("Select Role")}...`}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Agency")} isShow={true}>
                          <DropdownAsyncAgency
                            name="agency_id"
                            onChangeSelect={(event: any) => {
                              setAgencySearch(event);
                            }}
                            isMulti={false}
                            placeholder={`${t("Select Agency")}...`}
                            isShowIconCheck={false}
                            listCheckedUsers={agencySearch}
                            isAllOption={true}
                            isNotBelongToAgency={false}
                            isClearable={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-3 mt-md-2"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-2 "
                            onClick={searchData}
                            disabled={isUserLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <CardBody className="pt-3">
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={users?.items?.length ? users?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={users?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(users?.pagination?.total) /
                          Number(users?.meta?.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isUserLoading}
                    />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
        <ToastContainer closeButton={false} limit={1} />
        <Modal
          isOpen={!!userEdit?.id || isOpen}
          centered={true}
          size="lg"
          scrollable={true}
        >
          <ModalHeader toggle={onCloseClick}>
            <>
              {!!userEdit?.id
                ? t("Button Update Operator")
                : t("Button Create Operator")}
            </>{" "}
            <>{userEdit?.id && (" - " + (userEdit?.name || "") || "")}</>
          </ModalHeader>
          <ModalBody>
            <div style={{ minHeight: "calc(100dvh - 300px)" }}>
              <OperatorForm
                isModal={true}
                id={String(userEdit?.id || "")}
                isCopy={!!userEdit?.isCopy}
                triggerRefresh={triggerRefresh}
              />
            </div>
          </ModalBody>
        </Modal>
        <ModalConfirm
          header={t("Button Delete Operator")}
          isOpen={isOpenConfirm}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmClick}
          onConfirm={handleActionDelete}
        />
      </div>
    </React.Fragment>
  );
};

export default UserList;
